import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitDateFeedback, submitDateReceipt, getPostDateSuggestions } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Star, Receipt, ThumbsUp, ThumbsDown, Loader2, X, DollarSign, CheckCircle2, MessageCircle, Flower2, CalendarPlus, Heart, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';

const DateFeedbackModal = ({ datePlan, onClose, onSubmitted }: { datePlan: any; onClose: () => void; onSubmitted: () => void }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('feedback');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Feedback state
  const [overall, setOverall] = useState(0);
  const [adherence, setAdherence] = useState(0);
  const [fairness, setFairness] = useState(0);
  const [wouldAgain, setWouldAgain] = useState<boolean | null>(null);
  // DutchDate rule: each person pays for their own. Did both do so, as agreed?
  const [paidOwnShare, setPaidOwnShare] = useState<boolean | null>(null);
  const [dateHappened, setDateHappened] = useState<boolean | null>(true);
  const [comment, setComment] = useState('');

  // Reflection state
  const [reflections, setReflections] = useState<Record<string, string>>({});
  const [reflectionPrompts, setReflectionPrompts] = useState<any[]>([]);

  // Receipt state — only the user's OWN spend is recorded (no partner amount).
  const [myAmount, setMyAmount] = useState('');
  const [notes, setNotes] = useState('');

  // Suggestions state
  const [suggestions, setSuggestions] = useState<any[]>([]);

  useEffect(() => {
    import('../lib/api').then(({ getReflectionPrompts }) => {
      getReflectionPrompts().then(res => setReflectionPrompts(res.data || [])).catch(() => {});
    });
  }, []);

  const StarRating = ({ value, onChange, label }: { value: number; onChange: (v: number) => void; label: string }) => (
    <div className="space-y-1">
      <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            key={s}
            onClick={() => onChange(s)}
            className={`p-1 transition-colors ${s <= value ? 'text-dutch-orange' : 'text-neutral-200'}`}
          >
            <Star size={28} fill={s <= value ? 'currentColor' : 'none'} />
          </button>
        ))}
      </div>
    </div>
  );

  const handleSubmitFeedback = async () => {
    if (!overall || !adherence || !fairness || wouldAgain === null) {
      toast.error('Please complete all ratings');
      return;
    }
    setSubmitting(true);
    try {
      await submitDateFeedback({
        date_plan_id: datePlan.id,
        match_id: datePlan.match_id,
        overall_rating: overall,
        budget_adherence: adherence,
        fairness_rating: fairness,
        paid_own_share_as_agreed: paidOwnShare,
        date_happened: dateHappened ?? true,
        would_date_again: wouldAgain,
        comment: comment || null,
      });
      toast.success('Feedback submitted!');
      setStep('reflection');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReceipt = async () => {
    const my = parseFloat(myAmount);
    if (!my || my <= 0) {
      toast.error('Enter your amount');
      return;
    }
    setSubmitting(true);
    try {
      await submitDateReceipt({
        date_plan_id: datePlan.id,
        match_id: datePlan.match_id,
        my_amount: my,
        notes: notes || null,
      });
      toast.success('Receipt logged! Your Fair Score is updating.');
      // Fetch post-date suggestions
      try {
        const sugRes = await getPostDateSuggestions(datePlan.id, wouldAgain ?? false);
        // Filter out 'send_flowers' / 'flowers' suggestions — affiliate API not active
        const filtered = (sugRes.data?.suggestions || []).filter(
          (s: any) => s?.type !== 'flowers' && s?.action !== 'send_flowers'
        );
        setSuggestions(filtered);
      } catch {}
      setStep('suggestions');
    } catch (err) {
      toast.error('Failed to log receipt');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <motion.div className="relative bg-white rounded-3xl p-8 max-w-sm w-full text-center" initial={{ scale: 0.8 }} animate={{ scale: 1 }}>
          <CheckCircle2 size={48} className="text-dutch-teal mx-auto mb-4" />
          <h2 className="text-xl font-heading font-bold mb-2">All Done!</h2>
          <p className="text-sm text-muted-foreground mb-6">Your feedback helps build a fairer dating community.</p>
          <Button onClick={onClose} className="w-full bg-dutch-teal hover:bg-dutch-teal-dark text-white rounded-full">Close</Button>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div data-testid="date-feedback-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <motion.div className="relative bg-white rounded-3xl p-6 max-w-sm w-full max-h-[85vh] overflow-y-auto" initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}>
        <button onClick={onClose} className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600">
          <X size={20} />
        </button>

        {step === 'feedback' ? (
          <>
            <h2 className="text-xl font-heading font-bold mb-1">How was your date?</h2>
            <p className="text-sm text-muted-foreground mb-4">Your honest feedback builds a fairer community.</p>

            {/* Fair Partner Score banner */}
            <div className="bg-gradient-to-r from-dutch-teal/10 to-emerald-50 border border-dutch-teal/20 rounded-2xl p-3 mb-5 flex items-start gap-2">
              <ShieldCheck size={16} className="text-dutch-teal mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-neutral-900">This feeds the Fair Partner Score</p>
                <p className="text-[11px] text-neutral-600 leading-snug mt-0.5">Your answers — especially whether each person paid for their own, as agreed — determine whether your match earns the <span className="font-semibold">Fair Partner Badge</span>. Be honest. Subscribers see the badge on profiles.</p>
              </div>
            </div>

            <div className="space-y-5">
              {/* Did the date actually happen? Gates the rest */}
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Did the date actually happen?</Label>
                <div className="flex gap-3">
                  <button
                    data-testid="date-happened-yes"
                    onClick={() => setDateHappened(true)}
                    className={`flex-1 py-3 rounded-xl border-2 flex items-center justify-center gap-2 font-medium transition-all ${
                      dateHappened === true ? 'border-dutch-teal bg-teal-50 text-dutch-teal' : 'border-neutral-100 text-neutral-400'
                    }`}
                  >
                    <ThumbsUp size={18} /> Yes, we met
                  </button>
                  <button
                    data-testid="date-happened-no"
                    onClick={() => setDateHappened(false)}
                    className={`flex-1 py-3 rounded-xl border-2 flex items-center justify-center gap-2 font-medium transition-all ${
                      dateHappened === false ? 'border-rose-400 bg-rose-50 text-rose-500' : 'border-neutral-100 text-neutral-400'
                    }`}
                  >
                    <ThumbsDown size={18} /> No-show / Cancelled
                  </button>
                </div>
              </div>

              {dateHappened !== false && (
                <>
                  <StarRating value={overall} onChange={setOverall} label="Overall Experience" />
                  <StarRating value={adherence} onChange={setAdherence} label="Budget Adherence" />
                  <StarRating value={fairness} onChange={setFairness} label="Fairness of Payment" />

                  {/* Paid own share as agreed — direct fairness signal, weighted heavily in Fair Badge */}
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Did each of you pay for your own, as agreed?</Label>
                    <p className="text-[11px] text-neutral-500 -mt-1">On DutchDate, each person pays for their own items and expenses.</p>
                    <div className="flex gap-3">
                      <button
                        data-testid="paid-own-yes"
                        onClick={() => setPaidOwnShare(true)}
                        className={`flex-1 py-3 rounded-xl border-2 flex items-center justify-center gap-2 font-medium transition-all ${
                          paidOwnShare === true ? 'border-emerald-500 bg-emerald-50 text-emerald-600' : 'border-neutral-100 text-neutral-400'
                        }`}
                      >
                        <ThumbsUp size={18} /> Yes, fair
                      </button>
                      <button
                        data-testid="paid-own-no"
                        onClick={() => setPaidOwnShare(false)}
                        className={`flex-1 py-3 rounded-xl border-2 flex items-center justify-center gap-2 font-medium transition-all ${
                          paidOwnShare === false ? 'border-rose-400 bg-rose-50 text-rose-500' : 'border-neutral-100 text-neutral-400'
                        }`}
                      >
                        <ThumbsDown size={18} /> No, unfair
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Would you date again?</Label>
                    <div className="flex gap-3">
                      <button
                        data-testid="would-date-yes"
                        onClick={() => setWouldAgain(true)}
                        className={`flex-1 py-3 rounded-xl border-2 flex items-center justify-center gap-2 font-medium transition-all ${
                          wouldAgain === true ? 'border-dutch-teal bg-teal-50 text-dutch-teal' : 'border-neutral-100 text-neutral-400'
                        }`}
                      >
                        <ThumbsUp size={18} /> Yes
                      </button>
                      <button
                        data-testid="would-date-no"
                        onClick={() => setWouldAgain(false)}
                        className={`flex-1 py-3 rounded-xl border-2 flex items-center justify-center gap-2 font-medium transition-all ${
                          wouldAgain === false ? 'border-red-400 bg-red-50 text-red-500' : 'border-neutral-100 text-neutral-400'
                        }`}
                      >
                        <ThumbsDown size={18} /> No
                      </button>
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Comment (optional)</Label>
                <textarea
                  data-testid="feedback-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-sm resize-none"
                  rows={2}
                  placeholder={dateHappened === false ? 'What happened? (no-show, cancelled, etc.)' : 'Any thoughts...'}
                />
              </div>

              <Button
                data-testid="submit-feedback-button"
                onClick={handleSubmitFeedback}
                disabled={submitting || dateHappened === null || (dateHappened !== false && (overall === 0 || adherence === 0 || fairness === 0 || wouldAgain === null || paidOwnShare === null))}
                className="w-full h-12 bg-dutch-teal hover:bg-dutch-teal-dark text-white rounded-full font-heading font-semibold disabled:opacity-50"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : 'Submit Feedback'}
              </Button>
            </div>
          </>
        ) : step === 'reflection' ? (
          <>
            <div className="flex items-center gap-2 mb-1">
              <MessageCircle size={20} className="text-violet-500" />
              <h2 className="text-xl font-heading font-bold">Reflect on Your Date</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-5">Take a moment for self-reflection. This is just for you.</p>
            <div className="space-y-4 max-h-[40vh] overflow-y-auto">
              {reflectionPrompts.map((rp) => (
                <div key={rp.id} className="space-y-1.5">
                  <p className="text-sm font-medium text-neutral-800">{rp.prompt}</p>
                  <textarea
                    data-testid={`reflection-${rp.id}`}
                    value={reflections[rp.id] || ''}
                    onChange={(e) => setReflections(prev => ({ ...prev, [rp.id]: e.target.value }))}
                    className="w-full rounded-xl border border-neutral-200 bg-neutral-50 p-2.5 text-sm resize-none"
                    rows={2}
                    placeholder="Your thoughts..."
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-5">
              <Button variant="outline" onClick={() => setStep('receipt')} className="flex-1 rounded-full">Skip</Button>
              <Button
                data-testid="save-reflection-button"
                onClick={() => setStep('receipt')}
                className="flex-1 bg-violet-500 hover:bg-violet-600 text-white rounded-full font-heading"
              >
                Continue
              </Button>
            </div>
          </>
        ) : step === 'receipt' ? (
          <>
            <div className="flex items-center gap-2 mb-1">
              <Receipt size={20} className="text-dutch-orange" />
              <h2 className="text-xl font-heading font-bold">Date Receipt</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-2">Log what <span className="font-semibold">you</span> spent to build your Fair Score.</p>
            <div className="bg-teal-50/60 border border-teal-100 rounded-xl p-3 mb-5 flex items-start gap-2">
              <ShieldCheck size={14} className="text-dutch-teal mt-0.5 shrink-0" />
              <p className="text-[11px] text-dutch-teal-dark leading-snug">On DutchDate, each person pays for their own items — so you only record your own spend.</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Your Own Spend ($)</Label>
                <div className="relative">
                  <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <Input
                    data-testid="my-amount-input"
                    type="number"
                    step="0.01"
                    value={myAmount}
                    onChange={(e) => setMyAmount(e.target.value)}
                    className="pl-8 rounded-xl border-neutral-200 bg-neutral-50"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Notes (optional)</Label>
                <Input
                  data-testid="receipt-notes-input"
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="rounded-xl border-neutral-200 bg-neutral-50"
                  placeholder="e.g. dinner, tickets"
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { onClose(); if (onSubmitted) onSubmitted(); }} className="flex-1 rounded-full">
                  Skip
                </Button>
                <Button
                  data-testid="submit-receipt-button"
                  onClick={handleSubmitReceipt}
                  disabled={submitting}
                  className="flex-1 bg-dutch-orange hover:bg-pink-600 text-white rounded-full font-heading"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : 'Log Receipt'}
                </Button>
              </div>
            </div>
          </>
        ) : step === 'suggestions' ? (
          <>
            <div className="flex items-center gap-2 mb-1">
              <Heart size={20} className="text-rose-500" />
              <h2 className="text-xl font-heading font-bold">What's Next?</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              {wouldAgain ? "Great date! Here are some ideas:" : "Every experience matters. Here's what you can do:"}
            </p>
            <div className="space-y-3">
              {suggestions.map((s, idx) => {
                const icons = { flowers: Flower2, second_date: CalendarPlus, message: MessageCircle, kind_message: MessageCircle, reflect: Heart };
                const Icon = icons[s.type as keyof typeof icons] || Heart;
                return (
                  <motion.button
                    key={idx}
                    data-testid={`suggestion-${s.type}`}
                    onClick={() => {
                      if (s.url) window.open(s.url, '_blank');
                      else if (s.action === 'send_flowers') { onClose(); if (onSubmitted) onSubmitted(); navigate('/bookings?tab=flowers'); }
                      else if (s.action === 'plan_date') { onClose(); if (onSubmitted) onSubmitted(); navigate('/date-plan'); }
                      else if (s.action === 'open_chat') { onClose(); if (onSubmitted) onSubmitted(); navigate('/matches'); }
                      else if (s.action === 'blog') { onClose(); if (onSubmitted) onSubmitted(); navigate('/blog'); }
                      else { onClose(); if (onSubmitted) onSubmitted(); }
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-neutral-100 hover:border-dutch-teal/30 hover:bg-teal-50/30 text-left transition-all"
                  >
                    <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
                      <Icon size={18} className="text-rose-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-heading font-bold text-sm text-neutral-900">{s.title}</h4>
                      <p className="text-xs text-muted-foreground">{s.description}</p>
                    </div>
                    <span className="text-[10px] font-semibold text-dutch-teal whitespace-nowrap">{s.cta}</span>
                  </motion.button>
                );
              })}
            </div>
            <Button onClick={() => { setSubmitted(true); onClose(); if (onSubmitted) onSubmitted(); }} variant="outline" className="w-full mt-4 rounded-full">
              Done
            </Button>
          </>
        ) : null}
      </motion.div>
    </motion.div>
  );
};

export default DateFeedbackModal;
